import React, { useState } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { MapPin, Bed, Bath, Maximize, Heart, Loader2 } from 'lucide-react';
import { useData } from '../context/DataContext';

export const Properties = () => {
  const { properties, loading } = useData();
  const [likedProperties, setLikedProperties] = useState([]);
  const [filter, setFilter] = useState('all');

  const toggleLike = (id) => {
    setLikedProperties(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const filteredProperties = filter === 'all'
    ? properties.filter(p => p.featured)
    : properties.filter(p => p.type === filter && p.featured);

  const propertyTypes = ['all', 'Villa', 'Apartment', 'Penthouse', 'Duplex'];

  const handleWhatsAppInquiry = (property) => {
    const message = `Hi, I'm interested in ${property.title} located at ${property.location}. Price: ${property.price}. Can you provide more details?`;
    window.open(`https://wa.me/918667644336?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <section id="properties" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 animate-fade-in-up">
          <div className="inline-block bg-[#D4AF37]/10 text-[#D4AF37] px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Featured Properties
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-[#0F1419] mb-6 playfair">
            Discover Premium Properties
          </h2>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto">
            Handpicked selection of luxury homes in Coimbatore's most prestigious neighborhoods
          </p>
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {propertyTypes.map((type) => (
            <Button
              key={type}
              onClick={() => setFilter(type)}
              variant={filter === type ? 'default' : 'outline'}
              className={`px-6 py-2 rounded-full font-medium transition-all ${filter === type
                ? 'bg-[#D4AF37] hover:bg-[#B8941F] text-white'
                : 'border-gray-300 text-gray-700 hover:border-[#D4AF37] hover:text-[#D4AF37]'
                }`}
            >
              {type === 'all' ? 'All Properties' : type}
            </Button>
          ))}
        </div>

        {/* Properties Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {loading ? (
            <div className="col-span-full flex justify-center py-20">
              <Loader2 className="w-12 h-12 text-[#D4AF37] animate-spin" />
            </div>
          ) : (
            <>
              {filteredProperties.map((property) => (
                <Card
                  key={property.id}
                  className="overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-300 smooth-hover group"
                >
                  {/* Image */}
                  <div className="relative image-zoom-container h-64">
                    <img
                      src={property.image}
                      alt={property.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4">
                      <Badge className="bg-[#D4AF37] text-white border-0 px-3 py-1">
                        {property.type}
                      </Badge>
                    </div>
                    <button
                      onClick={() => toggleLike(property.id)}
                      className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm p-2 rounded-full hover:bg-white transition-colors"
                    >
                      <Heart
                        className={`w-5 h-5 ${likedProperties.includes(property.id)
                          ? 'fill-red-500 text-red-500'
                          : 'text-gray-600'
                          }`}
                      />
                    </button>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="text-xl font-bold text-[#0F1419] playfair group-hover:text-[#D4AF37] transition-colors">
                        {property.title}
                      </h3>
                    </div>
                    <div className="flex items-center text-gray-600 mb-4">
                      <MapPin className="w-4 h-4 mr-2 text-[#D4AF37]" />
                      <span className="text-sm">{property.location}</span>
                    </div>

                    {/* Property Details */}
                    <div className="flex items-center justify-between py-4 border-t border-b border-gray-100 mb-4">
                      <div className="flex items-center space-x-1 text-gray-600">
                        <Bed className="w-4 h-4" />
                        <span className="text-sm font-medium">{property.bedrooms}</span>
                      </div>
                      <div className="flex items-center space-x-1 text-gray-600">
                        <Bath className="w-4 h-4" />
                        <span className="text-sm font-medium">{property.bathrooms}</span>
                      </div>
                      <div className="flex items-center space-x-1 text-gray-600">
                        <Maximize className="w-4 h-4" />
                        <span className="text-sm font-medium">{property.area}</span>
                      </div>
                    </div>

                    {/* Price & CTA */}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Price</p>
                        <p className="text-2xl font-bold text-[#D4AF37] playfair">{property.price}</p>
                      </div>
                      <Button
                        onClick={() => handleWhatsAppInquiry(property)}
                        className="bg-[#0F1419] hover:bg-[#1a2634] text-white px-6"
                      >
                        Inquire Now
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </>
          )}
        </div>

        {/* View All CTA */}
        <div className="text-center mt-12">
          <Button
            onClick={() => window.open('https://wa.me/918667644336?text=I want to see more properties', '_blank')}
            variant="outline"
            size="lg"
            className="border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white px-8 py-6 text-lg font-semibold"
          >
            View All Properties
          </Button>
        </div>
      </div>
    </section>
  );
};